/* eslint-disable @typescript-eslint/camelcase */

import React from "react";
import BuyOption, {
    BuyOptionProps,
} from "web/react/components/buybuybuy-area/buy-option/buy-option";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import SizeOptionsForm from "web/react/components/buybuybuy-area/size-options-form/size-options-form";
import AffiliateOptions, {
    AffiliateOptionsProps,
} from "web/react/components/buybuybuy-area/affiliate-options/affiliate-options";
import StickyBuyButton from "web/react/components/buybuybuy-area/sticky-buy-button/sticky-buy-button";
import requester from "web/script/modules/requester";
import environment from "web/script/modules/environment";
import facebookEvents from "web/script/analytics/facebook-events";
import logging from "web/script/utils/logging";
import analytics from "web/script/analytics/analytics";
import globalEvents from "web/script/utils/global-events";
import _map from "lodash/map";
import styles from "./buy-options.css";

const ADD_TO_CART_ENDPOINT = "/cart/add/";
const ONE_CLICK_CHECKOUT = "/cart/one-click-checkout/";

interface BuyOptionsProps {
    isOverlay?: boolean;
    withSearch?: boolean;
    iconOptions?: BuyOptionProps[];
    affiliateOptions?: AffiliateOptionsProps;
    productId: string;
    isSavedForLater: "true" | "false";
    sizeOptionsForm: any;
}

interface CallEndpointOptions {
    raw_size: string;
    link_id: string;
}

const FINAL_COUNT = 300 + Math.ceil(Math.random() * 150);

function BuyOptions({
    isOverlay,
    withSearch,
    affiliateOptions,
    iconOptions,
    sizeOptionsForm,
    productId,
    isSavedForLater,
}: BuyOptionsProps): React.ReactElement {
    const [adjustedStoresTip, setAdjustedStoresTip] = React.useState<string>(
        sizeOptionsForm.storesTip
    );
    const [selectedSize, setSelectedSize] = React.useState<{ raw_size: string } | null>(null);
    const [initialLoaded, setInitialLoaded] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [barLoading, setBarLoading] = React.useState<boolean>(false);
    const [count, setCount] = React.useState<number>(0);
    const optionsRef = React.useRef<HTMLDivElement>(null);

    function sendBuyActionAnalytics(options): Promise<any[]> {
        const label =
            isSavedForLater === "true" ? "save_for_later_add_to_cart" : "add_to_cart_button";

        const analyticsPromises = [
            analytics.ecommerce("ec:addProduct", {
                name: environment.get("analyticsProduct.product_canonical_slug"),
                category: environment.get("analyticsProduct.ga_category"),
                brand: environment.get("analyticsProduct.designer_slug"),
                variant: environment.get("analyticsProduct.product_uid"),
                price: options.price,
                quantity: 1,
                dimension20: environment.get("analyticsProduct.gender"),
                dimension29: environment.get("analyticsProduct.color"),
                dimension28: true,
                dimension30: false,
                dimension32: "Lyst",
                dimension34: options.raw_size,
                dimension23: options.retailer_slug,
                metric8: options.sale_discount,
                dimension31: options.is_final_sale,
                dimension27: options.has_free_shipping,
            }),
            analytics.ecommerce("ec:setAction", "add"),
            analytics.event("CART", "ADD_ITEM", label),
            analytics.tagEvent(
                analytics.TAG_NETWORKS.FACEBOOK,
                facebookEvents.ADD_TO_CART,
                {
                    content_ids: [productId],
                    content_type: "product",
                },
                true,
                false
            ),
        ];
        return Promise.all(analyticsPromises);
    }

    async function callEndpoint(
        endpoint: string,
        options: CallEndpointOptions,
        onSuccess: Function,
        onError: Function
    ): Promise<Response> {
        const { raw_size, link_id } = options;

        setErrorMessage(null);
        sendBuyActionAnalytics(options).catch((err) => {
            logging.error(err);
        });

        const requestData = {
            method: "POST",
            body: {
                buy_option: JSON.stringify({
                    icon: true,
                    raw_size,
                    link_id,
                }),
                is_base64_encoded: false,
            },
        };

        return requester(endpoint, requestData as any)
            .then(requester.toJSON)
            .then((response) => {
                if (!response.success) {
                    let error = new Error() as any;
                    error.response = response;
                    throw error;
                } else {
                    return onSuccess(response);
                }
            })
            .catch((err) => {
                logging.error(err.response);
                return onError(err);
            });
    }

    async function onAddToBag(options): Promise<Response> {
        function onSuccess(): boolean {
            globalEvents.trigger("added-to-bag");
            return true;
        }

        function onError(err: any): any {
            analytics.event("CART", "ADD_TO_BAG_ERROR");
            let errorMessage = "Something went wrong!";

            if (err.response) {
                if (err.response["data"]) {
                    errorMessage = _map(err.response["data"], "message").join(" ");
                }

                if (
                    errorMessage.indexOf(
                        "something went wrong trying to add this product to your cart"
                    ) > -1
                ) {
                    analytics.event("cart", "add_to_cart_error", "cart_add_item_error");
                } else if (
                    errorMessage.indexOf("This product seems to no longer be in stock") > -1
                ) {
                    analytics.event("cart", "add_to_cart_error", "product_no_longer_in_stock");
                } else if (errorMessage.indexOf("You need to select an option") > -1) {
                    analytics.event("cart", "add_to_cart_error", "no_buy_option_detected");
                } else if (errorMessage.indexOf("You need to select a size") > -1) {
                    analytics.event("cart", "add_to_cart_error", "no_size_detected");
                } else if (errorMessage.indexOf("you cannot have more than") > -1) {
                    analytics.event("cart", "add_to_cart_error", "too_many_items_in_cart");
                }
            }
            setErrorMessage(errorMessage);
            return err;
        }

        return callEndpoint(ADD_TO_CART_ENDPOINT, options, onSuccess, onError);
    }

    async function oneClickCheckout(options): Promise<Response> {
        function onSuccess(response): boolean {
            window.location = response.data.url;
            return true;
        }

        function onError(): void {
            let errorMessage = "Something went wrong with checkout!";
            setErrorMessage(errorMessage);
        }
        return callEndpoint(ONE_CLICK_CHECKOUT, options, onSuccess, onError);
    }

    function onSizeChange(newSize): void {
        setErrorMessage(null);
        setSelectedSize(newSize);
    }

    function incrementToMax(newCount): void {
        const nextCount = Math.ceil(newCount + Math.random() * 10);
        setCount(nextCount);
        if (nextCount < FINAL_COUNT) {
            setTimeout(() => {
                incrementToMax(nextCount);
            }, 0);
        }
    }

    React.useEffect(() => {
        let timeout;
        if (selectedSize) {
            let storesSelling = 0;
            if (iconOptions) {
                storesSelling += iconOptions.filter(
                    (option) => option.raw_size === selectedSize.raw_size
                ).length;
            }
            if (affiliateOptions) {
                storesSelling += affiliateOptions.options.length;
            }

            if (withSearch && !initialLoaded) {
                timeout = setTimeout(() => {
                    setAdjustedStoresTip(
                        sizeOptionsForm.storesTip.replace(/[0-9]+/g, String(storesSelling))
                    );
                    setInitialLoaded(true);
                    setLoaded(true);
                    globalEvents.trigger("buy-options-size-change");
                }, 1400);

                setTimeout(() => {
                    setBarLoading(true);
                }, 50);

                incrementToMax(count);
            } else {
                setInitialLoaded(true);
                setLoaded(false);

                timeout = setTimeout(() => {
                    setAdjustedStoresTip(
                        sizeOptionsForm.storesTip.replace(/[0-9]+/g, String(storesSelling))
                    );
                    setLoaded(true);
                    globalEvents.trigger("buy-options-size-change");
                }, 600);
            }
        }

        return (): void => {
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSize]);

    const filteredIconOptions = !selectedSize
        ? iconOptions
        : iconOptions
        ? iconOptions.filter((option) => option.raw_size === selectedSize.raw_size)
        : [];

    return (
        <>
            {!isOverlay && <StickyBuyButton targetRef={optionsRef} />}
            <SizeOptionsForm
                action={sizeOptionsForm.action}
                csrfToken={sizeOptionsForm.csrfToken}
                country={sizeOptionsForm.country}
                returnUrl={sizeOptionsForm.returnUrl}
                availableSizes={sizeOptionsForm.availableSizes}
                storesTip={adjustedStoresTip}
                sizes={sizeOptionsForm.sizes}
                onSizeChange={onSizeChange}
            />
            {errorMessage && (
                <div className={`${styles.errorArea} mt20`}>
                    <em>
                        {errorMessage}
                        {/* todo I18N-291 */}
                    </em>
                </div>
            )}
            <div className={`${styles.options} buybuybuy-options mt25`} ref={optionsRef}>
                {/* TODO: Refactor buybuybuy-options className when migrating sticky buy button */}
                {selectedSize && (
                    <>
                        {withSearch && !initialLoaded && (
                            <div className={styles.search}>
                                <h5 className={styles.text}>
                                    {"Searching "}
                                    <span className={styles.count}>{count}</span>
                                    {" stores..."}
                                </h5>
                                <div
                                    className={`${styles.bar} ${
                                        barLoading ? styles.barLoading : ""
                                    }`}
                                />
                            </div>
                        )}
                        {initialLoaded && !loaded && (
                            <div className={styles.spinner}>
                                <LoadingSpinner inline type="highlight" />
                            </div>
                        )}
                        {loaded && (
                            <div className={styles.options}>
                                {filteredIconOptions &&
                                    filteredIconOptions.map((iconOption, idx) => (
                                        <BuyOption
                                            key={idx}
                                            initialExpanded={idx === 0}
                                            {...iconOption}
                                            onAddToBag={onAddToBag}
                                            oneClickCheckout={oneClickCheckout}
                                        />
                                    ))}
                                {affiliateOptions && (
                                    <AffiliateOptions
                                        title={affiliateOptions.title}
                                        options={affiliateOptions?.options}
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}

export default BuyOptions;
